import { useState, useMemo, useRef, useEffect } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { AccountingEngine } from '../services/accountingEngine';
import { formatDate } from '../lib/dateUtils';
import { useWheelNavigation } from '../hooks/useWheelNavigation';

export const SimpleReportView = ({ activeMenuLevel, handleItemSelect }: any) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [outFilter, setOutFilter] = useState<'All' | 'Debtors' | 'Creditors' | 'Overdue'>('All');

  // Determine what type of report this is
  const isOutstanding = activeMenuLevel === 'display_outstanding';
  const isStatistics = activeMenuLevel === 'display_statistics';
  const isStockFlow = activeMenuLevel === 'display_stockmovement';
  const isStockCheck = activeMenuLevel === 'display_stockquery';
  const isCostCalculator = activeMenuLevel === 'display_costestimation';
  const isPendingSales = activeMenuLevel === 'display_salesorderoutstanding';
  const isPendingPurchases = activeMenuLevel === 'display_purchaseorderoutstanding';
  const isLowStock = activeMenuLevel === 'display_reorderstatus';

  // Fetch all necessary data for simple calculations
  const ledgers = useLiveQuery(() => activeCompany ? db.ledgers.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const groups = useLiveQuery(() => activeCompany ? db.groups.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const stockItems = useLiveQuery(() => activeCompany ? db.stockItems.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const vouchers = useLiveQuery(() => activeCompany ? db.vouchers.where('companyId').equals(activeCompany.id!).filter(v => !v.deleted).toArray() : [], [activeCompany]);
  const voucherEntries = useLiveQuery(() => db.voucherEntries.toArray()); // Simplified fetching
  const inventoryEntries = useLiveQuery(() => db.inventoryEntries.toArray());
  const voucherTypes = useLiveQuery(() => activeCompany ? db.voucherTypes.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);

  const isLoading = !ledgers || !groups || !stockItems || !vouchers || !voucherEntries || !inventoryEntries || !voucherTypes;

  const reportData = useMemo(() => {
    if (!activeCompany || !ledgers || !groups || !stockItems || !vouchers || !voucherEntries || !inventoryEntries || !voucherTypes) return [];

    let items: any[] = [];

    if (isOutstanding) {
      const getDescendantGroups = (parentId: number) => {
        let childIds: number[] = [];
        const children = groups.filter(g => g.parentGroupId === parentId);
        for (const child of children) {
          childIds.push(child.id!);
          childIds = childIds.concat(getDescendantGroups(child.id!));
        }
        return childIds;
      };

      const debtorsGrp = groups.find(g => g.name === 'Sundry Debtors');
      const creditorsGrp = groups.find(g => g.name === 'Sundry Creditors');

      const targetGroupIds = new Set<number>();
      if (debtorsGrp) { targetGroupIds.add(debtorsGrp.id!); getDescendantGroups(debtorsGrp.id!).forEach(id => targetGroupIds.add(id)); }
      if (creditorsGrp) { targetGroupIds.add(creditorsGrp.id!); getDescendantGroups(creditorsGrp.id!).forEach(id => targetGroupIds.add(id)); }

      const targetLedgers = ledgers.filter(l => l.groupId && targetGroupIds.has(l.groupId));
      const today = new Date();

      // Build paid map from bill allocations on Receipt/Payment vouchers
      const paidByBill = new Map<number, number>(); // billId → total allocated
      vouchers.forEach(v => {
        if (!['Receipt', 'Payment'].includes(v.type)) return;
        const allocs = (v as any).billAllocations || [];
        allocs.forEach((a: any) => {
          if (a.billId && a.type === 'against_ref') {
            paidByBill.set(a.billId, (paidByBill.get(a.billId) || 0) + a.allocAmount);
          }
        });
      });

      targetLedgers.forEach(ledger => {
        const entries = voucherEntries.filter(e => e.ledgerId === ledger.id);

        let totalDr = ledger.balanceType === 'Dr' ? (ledger.openingBalance || 0) : 0;
        let totalCr = ledger.balanceType === 'Cr' ? (ledger.openingBalance || 0) : 0;
        let aging0_30 = 0, aging31_60 = 0, aging61_90 = 0, aging90plus = 0;

        entries.forEach(entry => {
          const v = vouchers.find(v => v.id === entry.voucherId);
          if (v && v.date >= fromDate && v.date <= toDate) {
            if (entry.type === 'Dr') totalDr += entry.amount;
            if (entry.type === 'Cr') totalCr += entry.amount;

            // For invoice aging — use net-of-allocated amounts if available
            const isInvoice = ['Sales','Purchase','Debit Note','Credit Note'].includes(v.type);
            let netEntry = entry.type === 'Dr' ? entry.amount : -entry.amount;
            if (isInvoice && paidByBill.has(v.id!)) {
              const paid = paidByBill.get(v.id!) || 0;
              // Reduce aging by paid proportion
              const share = entry.amount > 0 ? Math.min(paid, entry.amount) : 0;
              netEntry = entry.type === 'Dr' ? (entry.amount - share) : -(entry.amount - share);
            }

            const daysDiff = Math.floor((today.getTime() - new Date(v.date).getTime()) / (1000 * 60 * 60 * 24));
            if (daysDiff <= 30) aging0_30 += netEntry;
            else if (daysDiff <= 60) aging31_60 += netEntry;
            else if (daysDiff <= 90) aging61_90 += netEntry;
            else aging90plus += netEntry;
          }
        });

        let balance = 0;
        if (totalDr >= totalCr) {
          balance = totalDr - totalCr;
        } else {
          balance = totalCr - totalDr;
          aging0_30 = Math.abs(aging0_30); aging31_60 = Math.abs(aging31_60);
          aging61_90 = Math.abs(aging61_90); aging90plus = Math.abs(aging90plus);
        }

        const isDebtor = debtorsGrp && targetGroupIds.has(ledger.groupId!) &&
          !(creditorsGrp && getDescendantGroups(creditorsGrp.id!).includes(ledger.groupId!));
        const type = isDebtor ? 'Receivable' : 'Payable';

        if (balance > 0.005) {
          items.push({ party: ledger.name, type, balance, '0-30d': Math.max(0, aging0_30), '31-60d': Math.max(0, aging31_60), '61-90d': Math.max(0, aging61_90), '90+d': Math.max(0, aging90plus) });
        }
      });

      items.sort((a: any, b: any) => b.balance - a.balance);
    }

    if (isStatistics) {
      // Create dynamic tracking for any voucher type observed in the target period
      const statsMap: Record<string, number> = {};
      const countMap: Record<string, number> = {};
      let totalVouchersProcessed = 0;
      
      vouchers.forEach(v => {
        if (v.date >= fromDate && v.date <= toDate) {
          if (!statsMap[v.type]) { statsMap[v.type] = 0; countMap[v.type] = 0; }
          totalVouchersProcessed++;
          countMap[v.type]++;
          
          const vEntries = voucherEntries.filter(e => e.voucherId === v.id);
          // For a given voucher, sum of Dr should equal sum of Cr. We just take total Dr volume as standard transaction size.
          const drEntries = vEntries.filter(e => e.type === 'Dr');
          const totalVol = drEntries.reduce((acc, curr) => acc + curr.amount, 0);
          
          statsMap[v.type] += totalVol;
        }
      });

      // Transform map to array
      items = Object.keys(statsMap).map(type => ({
        type: `${type} (${countMap[type]})`, 
        total: statsMap[type]
      })).sort((a,b) => b.total - a.total); // Sort by volume
      
      // Add general stats
      items.unshift({ type: 'TOTAL VOUCHERS', total: totalVouchersProcessed });
      items.unshift({ type: 'TOTAL LEDGERS', total: ledgers.length });
    }

    if (isStockFlow) {
      // Build a map of voucherType name → stock direction for custom types
      const vtDirectionMap = new Map<string, 'In' | 'Out'>();
      voucherTypes.forEach(vt => {
        if (vt.affectsStock && vt.stockDirection) {
          vtDirectionMap.set(vt.name, vt.stockDirection as 'In' | 'Out');
        }
      });

      const getStockDirection = (vType: string): 'In' | 'Out' | null => {
        if (vtDirectionMap.has(vType)) return vtDirectionMap.get(vType)!;
        // Built-in defaults
        if (vType === 'Purchase' || vType === 'Receipt Note' || vType === 'Material In') return 'In';
        if (vType === 'Sales' || vType === 'Delivery Note' || vType === 'Material Out' || vType === 'Payment') return 'Out';
        return null;
      };

      stockItems.forEach(item => {
        let opening = item.openingBalance;
        let inQty = 0;
        let outQty = 0;

        const entries = inventoryEntries.filter(e => e.stockItemId === item.id);
        entries.forEach(entry => {
          const v = vouchers.find(v => v.id === entry.voucherId);
          if (v && v.date <= toDate) {
            const dir = getStockDirection(v.type);
            if (dir === 'In') inQty += entry.quantity;
            else if (dir === 'Out') outQty += entry.quantity;
          }
        });

        const closing = opening + inQty - outQty;

        if (opening > 0 || inQty > 0 || outQty > 0 || closing > 0) {
          items.push({ item: item.name, opening, in: inQty, out: outQty, closing });
        }
      });
    }

    if (isStockCheck) {
      // Build direction map same as above
      const vtDirectionMap = new Map<string, 'In' | 'Out'>();
      voucherTypes.forEach(vt => {
        if (vt.affectsStock && vt.stockDirection) {
          vtDirectionMap.set(vt.name, vt.stockDirection as 'In' | 'Out');
        }
      });

      const getStockDirection = (vType: string): 'In' | 'Out' | null => {
        if (vtDirectionMap.has(vType)) return vtDirectionMap.get(vType)!;
        if (vType === 'Purchase' || vType === 'Receipt Note' || vType === 'Material In') return 'In';
        if (vType === 'Sales' || vType === 'Delivery Note' || vType === 'Material Out') return 'Out';
        return null;
      };

      stockItems.forEach(item => {
        let currentQty = item.openingBalance || 0;
        const entries = inventoryEntries.filter(e => e.stockItemId === item.id);
        entries.forEach(entry => {
          const v = vouchers.find(v => v.id === entry.voucherId);
          if (v && v.date <= toDate) {
            const dir = getStockDirection(v.type);
            if (dir === 'In') currentQty += entry.quantity;
            else if (dir === 'Out') currentQty -= entry.quantity;
          }
        });
        items.push({ item: item.name, stock: currentQty, status: currentQty > 0 ? 'In Stock' : 'Out of Stock' });
      });
    }

    if (isCostCalculator) {
      // Build direction map for custom voucher types
      const vtDirectionMap = new Map<string, 'In' | 'Out'>();
      voucherTypes.forEach(vt => {
        if (vt.affectsStock && vt.stockDirection) {
          vtDirectionMap.set(vt.name, vt.stockDirection as 'In' | 'Out');
        }
      });
      const getStockDir = (vType: string): 'In' | 'Out' | null => {
        if (vtDirectionMap.has(vType)) return vtDirectionMap.get(vType)!;
        if (vType === 'Purchase' || vType === 'Receipt Note' || vType === 'Material In') return 'In';
        if (vType === 'Sales' || vType === 'Delivery Note' || vType === 'Material Out' || vType === 'Payment') return 'Out';
        return null;
      };

      stockItems.forEach(item => {
        let currentQty = item.openingBalance;
        let totalCostValue = item.openingValue; // Value based on opening

        const entries = inventoryEntries.filter(e => e.stockItemId === item.id);
        
        // Simple Weighted Average Cost Calculation
        entries.forEach(entry => {
          const v = vouchers.find(v => v.id === entry.voucherId);
          if (v && v.date <= toDate) {
            const dir = getStockDir(v.type);
            if (dir === 'In') {
              currentQty += entry.quantity;
              totalCostValue += entry.amount; // Inward brings value
            } else if (dir === 'Out') {
              // Outward doesn't change unit cost, but reduces total value proportionally based on avg cost
              const avgCost = currentQty + entry.quantity > 0 ? totalCostValue / (currentQty + entry.quantity) : 0;
              currentQty -= entry.quantity;
              totalCostValue -= entry.quantity * avgCost;
            }
          }
        });
        
        const avgRate = currentQty > 0 ? (totalCostValue / currentQty) : (item.openingRate || 0);
        
        items.push({ item: item.name, qty: currentQty, rate: avgRate, total: totalCostValue });
      });
    }

    if (isPendingSales || isPendingPurchases) {
      const getDescendantGroups = (parentId: number) => {
        let childIds: number[] = [];
        const children = groups.filter(g => g.parentGroupId === parentId);
        for (const child of children) {
          childIds.push(child.id!);
          childIds = childIds.concat(getDescendantGroups(child.id!));
        }
        return childIds;
      };

      const groupName = isPendingSales ? 'Sundry Debtors' : 'Sundry Creditors';
      const targetGrp = groups.find(g => g.name === groupName);
      
      if (targetGrp) {
        const targetGroupIds = new Set<number>([targetGrp.id!, ...getDescendantGroups(targetGrp.id!)]);
        const targetLedgers = ledgers.filter(l => l.groupId && targetGroupIds.has(l.groupId));

        targetLedgers.forEach(ledger => {
          const entries = voucherEntries.filter(e => e.ledgerId === ledger.id);
          
          let total = 0;
          let paid = 0;

          entries.forEach(entry => {
            const v = vouchers.find(v => v.id === entry.voucherId);
            if (!v || v.date > toDate) return; // Only consider up to toDate
            if (isPendingSales) {
              // Debtors: debited on Sales (invoice raised), credited on Receipt (payment received)
              if (entry.type === 'Dr') total += entry.amount;
              if (entry.type === 'Cr') paid += entry.amount;
            } else {
              // Creditors: credited on Purchase (bill received), debited on Payment (payment made)
              if (entry.type === 'Cr') total += entry.amount;
              if (entry.type === 'Dr') paid += entry.amount;
            }
          });

          const pending = total - paid;
          
          if (total > 0) {
             items.push({
               name: ledger.name,
               total,
               paid,
               pending
             });
          }
        });
      }
    }

    if (isLowStock) {
      const vtDirMap = new Map<string, 'In' | 'Out'>();
      voucherTypes.forEach(vt => {
        if (vt.affectsStock && vt.stockDirection) vtDirMap.set(vt.name, vt.stockDirection as 'In' | 'Out');
      });
      const getDir = (vType: string): 'In' | 'Out' | null => {
        if (vtDirMap.has(vType)) return vtDirMap.get(vType)!;
        if (vType === 'Purchase' || vType === 'Receipt Note' || vType === 'Material In') return 'In';
        if (vType === 'Sales' || vType === 'Delivery Note' || vType === 'Material Out') return 'Out';
        return null;
      };
      stockItems.forEach(item => {
        let currentQty = item.openingBalance || 0;
        const entries = inventoryEntries.filter(e => e.stockItemId === item.id);
        entries.forEach(entry => {
          const v = vouchers.find(v => v.id === entry.voucherId);
          if (v && v.date <= toDate) {
            const dir = getDir(v.type);
            if (dir === 'In') currentQty += entry.quantity;
            else if (dir === 'Out') currentQty -= entry.quantity;
          }
        });
        
        const minLevel = item.minStockLevel || 0;
        const reorderLvl = item.reorderLevel || 0;

        if (reorderLvl > 0 || minLevel > 0) {
          if (currentQty <= minLevel) {
            items.push({ item: item.name, currentStock: currentQty, minimumRequired: Math.max(minLevel, reorderLvl), status: 'Critical' });
          } else if (currentQty <= reorderLvl) {
            items.push({ item: item.name, currentStock: currentQty, minimumRequired: reorderLvl, status: 'Low' });
          }
        } else {
          // simple heuristic: less than 10 is low stock
          if (currentQty < 10 && currentQty > 0) {
            items.push({ item: item.name, currentStock: currentQty, minimumRequired: 10, status: 'Low' });
          } else if (currentQty <= 0) {
            items.push({ item: item.name, currentStock: currentQty, minimumRequired: 10, status: 'Critical' });
          }
        }
      });
    }

    return items;
  }, [activeCompany, isOutstanding, isStatistics, isStockFlow, isStockCheck, isCostCalculator, isPendingSales, isPendingPurchases, isLowStock, ledgers, groups, stockItems, vouchers, voucherEntries, inventoryEntries, voucherTypes, fromDate, toDate]);

  useWheelNavigation(scrollRef, reportData.length, setSelectedIndex, false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'q' || e.key === 'Escape') {
        e.preventDefault();
        handleItemSelect({ name: 'Quit' });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleItemSelect]);

  let title = '';
  let headers: string[] = [];

  if (isOutstanding) { title = 'Outstanding — Aging Analysis'; headers = ['Party', 'Type', 'Balance', '0-30 Days', '31-60 Days', '61-90 Days', '90+ Days']; }
  if (isStatistics) { title = 'Summary'; headers = ['Type', 'Total Amount']; }
  if (isStockFlow) { title = 'Stock Flow'; headers = ['Item', 'Opening', 'In', 'Out', 'Closing']; }
  if (isStockCheck) { title = 'Check Stock'; headers = ['Item', 'Stock', 'Status']; }
  if (isCostCalculator) { title = 'Cost Calculator'; headers = ['Item', 'Qty', 'Rate', 'Total Value']; }
  if (isPendingSales) { title = 'Sales Outstanding (Receivables)'; headers = ['Customer Name', 'Total Invoiced', 'Received Amount', 'Pending Amount']; }
  if (isPendingPurchases) { title = 'Purchase Outstanding (Payables)'; headers = ['Supplier Name', 'Total Billed', 'Paid Amount', 'Pending Amount']; }
  if (isLowStock) { title = 'Low Stock Alert'; headers = ['Item', 'Current Stock', 'Min. Required', 'Status']; }

  // Full-page layout imitating DayBookView
  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black pb-4">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10">
        <span className="w-1/3 text-left">{title}</span>
        <span className="w-1/3 text-center">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="bg-white text-black flex justify-between px-2 py-1 font-bold border-b border-[#aebec8] z-10 shadow-sm">
        <span>
          {isOutstanding ? 'Debtors & Creditors Aging' : 'Simplified Data View'}
        </span>
        {(isOutstanding || isStatistics || isPendingSales || isPendingPurchases)
          ? <span>{formatDate(fromDate)} to {formatDate(toDate)}</span>
          : <span>As On: {formatDate(toDate)}</span>
        }
      </div>

      {/* Outstanding: Filter Tabs */}
      {isOutstanding && (
        <div className="flex items-center border-b border-[#aebec8] shrink-0 bg-white">
          {(['All', 'Debtors', 'Creditors', 'Overdue'] as const).map(f => (
            <button key={f}
              onClick={() => setOutFilter(f)}
              className={`px-4 py-1 text-[11px] font-bold border-r border-[#aebec8] ${outFilter === f ? 'bg-[#1e8585] text-white' : 'bg-white text-gray-600 hover:bg-[#f0fafa]'}`}>
              {f}
            </button>
          ))}
          <span className="ml-auto px-2 text-[10px] text-gray-400">Q / Esc: Quit</span>
        </div>
      )}

      {/* Outstanding: Enhanced Header */}
      {isOutstanding ? (
        <div className="flex w-full px-2 py-1 font-bold text-[11px] border-b border-[#aebec8] bg-[#f0f9f9] shrink-0">
          <div className="w-[180px] flex-shrink-0">Party</div>
          <div className="w-[70px] flex-shrink-0">Type</div>
          <div className="flex-1 text-right">Balance (₹)</div>
          <div className="w-[90px] text-right text-green-700">0–30d</div>
          <div className="w-[90px] text-right text-amber-600">31–60d</div>
          <div className="w-[90px] text-right text-orange-600">61–90d</div>
          <div className="w-[90px] text-right text-red-700">90+d</div>
        </div>
      ) : (
        <div className="flex flex-col border-b border-[#aebec8] bg-white text-black font-bold px-2 py-1 shadow-sm">
          <div className="flex w-full">
            {headers.map((h, i) => (
              <div key={i} className={`flex-1 ${i > 0 && headers.length > 2 ? 'text-right' : 'text-left'}`}>{h}</div>
            ))}
          </div>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-white scrollbar-thin pb-6">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 font-bold italic flex flex-col items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e8585] mb-2"></div>
            Loading report data...
          </div>
        ) : reportData.length === 0 ? (
          <div className="p-8 text-center text-gray-500 font-bold italic">
            {isPendingPurchases ? 'No pending purchases' : isPendingSales ? 'No pending sales' : 'No data available to display for this report.'}
          </div>
        ) : isOutstanding ? (() => {
          // Enhanced outstanding render
          const filtered = reportData.filter((r: any) => {
            if (outFilter === 'Debtors')   return r.type === 'Receivable';
            if (outFilter === 'Creditors') return r.type === 'Payable';
            if (outFilter === 'Overdue')   return (r['90+d'] || 0) > 0 || (r['61-90d'] || 0) > 0;
            return true;
          });
          const totals = filtered.reduce((acc: any, r: any) => ({
            balance:  acc.balance  + (r.balance  || 0),
            d0_30:    acc.d0_30    + (r['0-30d'] || 0),
            d31_60:   acc.d31_60   + (r['31-60d'] || 0),
            d61_90:   acc.d61_90   + (r['61-90d'] || 0),
            d90plus:  acc.d90plus  + (r['90+d']   || 0),
          }), { balance: 0, d0_30: 0, d31_60: 0, d61_90: 0, d90plus: 0 });
          const fmtAmt = (n: number) => n > 0 ? n.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—';
          return (
            <>
              {filtered.map((r: any, idx: number) => {
                const isSel = selectedIndex === idx;
                const isOverdue = (r['90+d'] || 0) > 0;
                return (
                  <div key={idx}
                    className={`flex w-full px-2 py-[3px] border-b border-gray-100 cursor-pointer text-[11px] ${isSel ? 'bg-[#1e8585] text-white font-bold' : isOverdue ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-[#f0fafa]'}`}
                    onClick={() => setSelectedIndex(idx)}>
                    <div className="w-[180px] flex-shrink-0 font-semibold truncate">{r.party}</div>
                    <div className="w-[70px] flex-shrink-0">
                      <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${isSel ? 'text-white/80' : r.type === 'Receivable' ? 'text-teal-700 bg-teal-50' : 'text-orange-700 bg-orange-50'}`}>
                        {r.type === 'Receivable' ? 'DR' : 'CR'}
                      </span>
                    </div>
                    <div className={`flex-1 text-right font-bold tabular-nums ${isSel ? '' : r.type === 'Receivable' ? 'text-[#1e8585]' : 'text-orange-700'}`}>
                      {fmtAmt(r.balance)}
                    </div>
                    <div className={`w-[90px] text-right tabular-nums ${isSel ? '' : 'text-green-700'}`}>{fmtAmt(r['0-30d'])}</div>
                    <div className={`w-[90px] text-right tabular-nums ${isSel ? '' : 'text-amber-600'}`}>{fmtAmt(r['31-60d'])}</div>
                    <div className={`w-[90px] text-right tabular-nums ${isSel ? '' : 'text-orange-600'}`}>{fmtAmt(r['61-90d'])}</div>
                    <div className={`w-[90px] text-right tabular-nums font-bold ${isSel ? '' : 'text-red-700'}`}>{fmtAmt(r['90+d'])}</div>
                  </div>
                );
              })}
              {/* Totals row */}
              {filtered.length > 0 && (
                <div className="flex w-full px-2 py-1.5 border-t-2 border-[#aebec8] bg-[#f0fafa] text-[11px] font-bold sticky bottom-0">
                  <div className="w-[180px] flex-shrink-0 text-[#1e8585]">Total ({filtered.length})</div>
                  <div className="w-[70px] flex-shrink-0"></div>
                  <div className="flex-1 text-right text-[#1e8585] tabular-nums">{fmtAmt(totals.balance)}</div>
                  <div className="w-[90px] text-right text-green-700 tabular-nums">{fmtAmt(totals.d0_30)}</div>
                  <div className="w-[90px] text-right text-amber-600 tabular-nums">{fmtAmt(totals.d31_60)}</div>
                  <div className="w-[90px] text-right text-orange-600 tabular-nums">{fmtAmt(totals.d61_90)}</div>
                  <div className="w-[90px] text-right text-red-700 tabular-nums">{fmtAmt(totals.d90plus)}</div>
                </div>
              )}
            </>
          );
        })() : (
          reportData.map((row: any, idx: number) => {
            const values = Object.values(row);
            return (
              <div
                key={idx}
                className={`flex w-full px-2 py-[2px] cursor-pointer ${selectedIndex === idx ? 'bg-[#1e8585] text-white font-bold' : 'text-black hover:bg-gray-50'}`}
                onClick={() => setSelectedIndex(idx)}
              >
                {values.map((v: any, i: number) => (
                  <div key={i} className={`flex-1 ${i > 0 && values.length > 2 ? 'text-right' : 'text-left'} ${i === values.length - 1 && selectedIndex !== idx && typeof v === 'number' && v > 0 ? 'text-[#1e8585] font-bold' : ''}`}>
                    {typeof v === 'number' ? v.toFixed(2) : v}
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
      
      {/* Footer to mimic ERP footer */}
      <div className="flex border-t border-[#aebec8] bg-white h-[22px] w-full shadow-inner absolute bottom-0">
        {[{ key: 'Q', label: 'Quit' }].map((sc, i) => (
          <div key={i} className="flex-1 border-r border-[#aebec8] flex justify-between items-center px-1 cursor-pointer" onClick={() => handleItemSelect({ name: 'Quit' })}>
            <div className="flex items-center space-x-[2px] whitespace-nowrap">
              <span className="text-[#2a62a3] font-bold underline decoration-[#2a62a3] underline-offset-2">{sc.key[0]}</span>
              <span className="text-[#2a62a3] font-bold">:</span><span className="text-black leading-none">{sc.label}</span>
            </div>
            <span className="text-[#aebec8] text-[9px] mb-1">^</span>
          </div>
        ))}
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i+1} className="flex-1 border-r border-[#aebec8] opacity-50"></div>
        ))}
      </div>
    </div>
  );
};
